import React, { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import type { FallbackProps } from 'react-error-boundary'
import {
    IonButton,
    IonContent,
} from '@ionic/react'
import {
	errorReport,
	logEvent,
} from './firebase'
import { serializeError, ErrorObject } from 'serialize-error'
import Icon from './Icon'
import { EmailMax } from './Help'
import {
	useSection,
	useUser,
} from './data'
import packageInfo from '../package.json'
import './errorfallback.css'

export type ErrorReportRequest = {
    error: ErrorObject
    uid: UserId | undefined
    href: string | undefined
    section: Section
    version: string
    nativeType: 'Native' | 'Web'
}

function normalizeError(error: unknown) {
	return error instanceof Error ? error : new Error(String(error))
}

const ErrorFallback = (props: FallbackProps) => {

	const { error, resetErrorBoundary } = props
	const normalizedError = normalizeError(error)

	const user = useUser()
	const section = useSection()

	console.log('My error is:', normalizedError)

	useEffect(() => {

		const go = async () => {
			console.log("Reporting error to backend API:", normalizedError)

			const obj: ErrorReportRequest = {
				error: serializeError(normalizedError),
				uid: user?.uid,
				href: window?.location?.href,
				section,
				version: packageInfo?.version,
				nativeType: Capacitor.isNativePlatform() ? 'Native' : 'Web',
			}

			if (import.meta.env.DEV) {
				console.log("Not reporting error due to import.meta.env.DEV:", obj)
				return
			}

			try {
				// Log to my cloud Function... this is maybe not needed if Analytics is better
				const response = await errorReport(obj)
				console.log('Successfully reported error. Response:', response)

				// Also log with Analytics
				const result = await logEvent('error', {
					message: normalizedError.message,
				})
				console.log("Logged error event with Analytics:", result)

			} catch (err) {
				console.error("Problem logging error: ", err)
			}
		}

		go()
	}, [ normalizedError, user, section ])

	return (
		<IonContent
			className="error-boundary ion-padding"
		>
			<div className="error-title">
				Oh no! Something went wrong.
				<Icon />
			</div>
			<div className="let-max-know">
				If this problem persists, please let me know!
				<br />
				<EmailMax
					subject="AutoRoster error"
					text={"Error: " + normalizedError.message}
				/>
			</div>
            <p>
                <IonButton
                    onClick={resetErrorBoundary}
                >
                    Try Again
                </IonButton>
                <IonButton
                    href="/"
                >
                    Reload app
                </IonButton>
            </p>
            <fieldset>
				<legend>Details</legend>
				<p>
					<span className="error-subtitle">
						Error:
					</span>
					{normalizedError.message}
				</p>
				<p>
					<span className="error-subtitle">
						Page:
					</span>
					{window.location.href}
				</p>
				<p>
					<span className="error-subtitle">
						Stack:
					</span>
					{normalizedError.stack}
				</p>
			</fieldset>
		</IonContent>
	)
}

export default ErrorFallback
